import React, { useState, useEffect } from "react";

const Z = {
  primary:             "#55624d",
  primaryFixed:        "#d9e7cd",
  primaryFixedDim:     "#bdcbb2",
  onPrimary:           "#ffffff",
  surface:             "#f8faf3",
  onSurface:           "#191c18",
  onSurfaceVariant:    "#444841",
  outline:             "#757870",
  outlineVariant:      "#c5c8be",
};

// ─────────────────────────────────────────────
// PWA INSTALL BANNER
// Erscheint automatisch wenn die App installierbar ist.
// Auf iOS zeigt es eine Anleitung.
// ─────────────────────────────────────────────
export function PWAInstallBanner() {
  const [installPrompt, setInstallPrompt]   = useState(null);
  const [showBanner, setShowBanner]         = useState(false);
  const [showIOSGuide, setShowIOSGuide]     = useState(false);
  const [isInstalled, setIsInstalled]       = useState(false);

  useEffect(() => {
    // Bereits installiert?
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Bereits dismissed?
    const dismissed = localStorage.getItem("habitOS_pwaInstallDismissed");
    if (dismissed) return;

    // iOS Detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isIOS && isSafari) {
      // iOS zeigt eigene Anleitung
      setTimeout(() => setShowBanner(true), 3000);
      return;
    }

    // Android / Desktop: beforeinstallprompt Event
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
        setShowBanner(false);
      }
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem("habitOS_pwaInstallDismissed", "true");
  };

  if (isInstalled || !showBanner) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // iOS Anleitung
  if (showIOSGuide) {
    return (
      <div style={{
        position: "fixed", bottom: 90, left: 16, right: 16,
        background: Z.surface,
        borderRadius: 20, padding: "20px",
        boxShadow: "0 8px 32px rgba(85,98,77,0.2)",
        border: `1px solid ${Z.primaryFixedDim}`,
        zIndex: 1000,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 24 }}>📱</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface }}>
                App installieren
              </div>
              <div style={{ fontSize: 12, color: Z.onSurfaceVariant }}>
                Zum Homescreen hinzufügen
              </div>
            </div>
          </div>
          <button onClick={handleDismiss} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: Z.outline }}>✕</button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[
            { step: "1", icon: "⬆️", text: 'Tippe auf das Teilen-Symbol unten in Safari' },
            { step: "2", icon: "➕", text: '"Zum Home-Bildschirm" auswählen' },
            { step: "3", icon: "✅", text: '"Hinzufügen" tippen — fertig!' },
          ].map(({ step, icon, text }) => (
            <div key={step} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: Z.primaryFixed, borderRadius: 12 }}>
              <span style={{ fontSize: 20 }}>{icon}</span>
              <span style={{ fontSize: 13, color: Z.onSurface, lineHeight: 1.4 }}>{text}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Standard Install Banner
  return (
    <div style={{
      position: "fixed", bottom: 90, left: 16, right: 16,
      background: Z.primary,
      borderRadius: 20, padding: "16px 18px",
      boxShadow: "0 8px 32px rgba(85,98,77,0.3)",
      zIndex: 1000,
      display: "flex", alignItems: "center", gap: 14,
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      animation: "slideUp 0.3s ease forwards",
    }}>
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Icon */}
      <div style={{ width: 44, height: 44, borderRadius: 12, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>
        🌿
      </div>

      {/* Text */}
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: Z.onPrimary, marginBottom: 2 }}>
          Habit OS installieren
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
          Direkt vom Homescreen öffnen
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button onClick={handleDismiss} style={{ padding: "7px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.3)", background: "transparent", color: "rgba(255,255,255,0.8)", fontSize: 13, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Später
        </button>
        <button onClick={handleInstall} style={{ padding: "7px 14px", borderRadius: 10, border: "none", background: Z.onPrimary, color: Z.primary, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {isIOS ? "Anleitung" : "Installieren"}
        </button>
      </div>
    </div>
  );
}
